/**
 * Custom React Hook: useMidiRecorder
 * Manages Transport, Clock, Metronome, Count-In, Audio Playback & Raw Note Capture
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AudioEngine, globalAudioEngine } from '../core/audioEngine';
import { globalMidiAccess, MidiAccessManager } from '../core/midiAccess';
import { quantizeNotes } from '../core/quantize';
import {
  DEFAULT_PPQ,
  getTicksPerBeat,
  getTicksPerMeasure,
  msToTicks,
  ticksToMs,
  ticksToPosition,
} from '../core/timing';
import {
  LoopRange,
  MidiExportData,
  MidiNote,
  QuantizeOptions,
  TimeSignature,
  TransportPosition,
  TransportState,
} from '../types/midi';

export interface UseMidiRecorderOptions {
  bpm?: number;
  timeSignature?: TimeSignature;
  initialNotes?: MidiNote[];
  externalAudioContext?: AudioContext | null;
  externalMidiInput?: MIDIInput | null;
  onChange?: (data: MidiExportData) => void;
}

export function useMidiRecorder({
  bpm: initialBpm = 120,
  timeSignature: initialTimeSig = { beats: 4, beatType: 4 },
  initialNotes = [],
  externalAudioContext = null,
  externalMidiInput = null,
  onChange,
}: UseMidiRecorderOptions = {}) {
  // Config state
  const [bpm, setBpm] = useState<number>(initialBpm);
  const [timeSignature, setTimeSignature] = useState<TimeSignature>(initialTimeSig);
  const [ppq] = useState<number>(DEFAULT_PPQ);

  // Sync external props if they change
  useEffect(() => {
    if (initialBpm && initialBpm > 0) {
      setBpm((prev) => (prev !== initialBpm ? initialBpm : prev));
    }
  }, [initialBpm]);

  useEffect(() => {
    if (initialTimeSig?.beats && initialTimeSig?.beatType) {
      setTimeSignature((prev) => {
        if (prev.beats === initialTimeSig.beats && prev.beatType === initialTimeSig.beatType) {
          return prev;
        }
        return { beats: initialTimeSig.beats, beatType: initialTimeSig.beatType };
      });
    }
  }, [initialTimeSig?.beats, initialTimeSig?.beatType]);

  // Audio Engine reference — bind the shared external AudioContext synchronously during render,
  // NOT inside a useEffect. Effects commit bottom-up (children mount before parents), so doing
  // this in an effect here let child components (e.g. PianoEffectsPopover, InstrumentSelector)
  // race ahead on their OWN mount effect and lazily create a throwaway InstrumentManager bound
  // to a brand-new internal AudioContext (since the external one hadn't been assigned yet) —
  // which then got silently discarded a moment later when this ran, orphaning any subscription
  // already established on it. Symptom: FX sliders/presets looked frozen with zero console
  // errors, because EffectsChain.notify() kept firing correctly, just to listeners attached to
  // the wrong (discarded) EffectsChain instance. setExternalContext() is a cheap no-op once the
  // context already matches, so calling it unconditionally every render is safe.
  const audioEngineRef = useRef<AudioEngine>(globalAudioEngine);
  if (externalAudioContext) {
    audioEngineRef.current.setExternalContext(externalAudioContext);
  }

  // MIDI Access reference
  const midiAccessRef = useRef<MidiAccessManager>(globalMidiAccess);
  useEffect(() => {
    if (externalMidiInput) {
      midiAccessRef.current.setExternalInput(externalMidiInput);
    } else {
      midiAccessRef.current.setExternalInput(null);
    }
  }, [externalMidiInput]);

  // Live played pitches (for active visual piano key feedback)
  const [livePitches, setLivePitches] = useState<Set<number>>(new Set());
  const [lastMidiActivity, setLastMidiActivity] = useState<{
    pitch: number;
    velocity: number;
    channel: number;
    type: 'on' | 'off';
    timestamp: number;
  } | null>(null);

  // Transport settings
  const [transportState, setTransportState] = useState<TransportState>('stopped');
  const [currentTick, setCurrentTick] = useState<number>(0);
  const [metronomeEnabled, setMetronomeEnabled] = useState<boolean>(true);
  const [countInMeasures, setCountInMeasures] = useState<number>(1); // 0, 1 or 2
  const [countInBeat, setCountInBeat] = useState<number | null>(null);

  const [loopRange, setLoopRange] = useState<LoopRange>({
    enabled: false,
    startMeasure: 1,
    endMeasure: 4,
  });

  // Live refs to guarantee access to the latest values across 60fps animation frames & transport ticks
  const loopRangeRef = useRef<LoopRange>(loopRange);
  loopRangeRef.current = loopRange;

  const currentTickRef = useRef<number>(currentTick);
  currentTickRef.current = currentTick;

  const transportStateRef = useRef<TransportState>(transportState);
  transportStateRef.current = transportState;

  const bpmRef = useRef<number>(bpm);
  bpmRef.current = bpm;

  const timeSignatureRef = useRef<TimeSignature>(timeSignature);
  timeSignatureRef.current = timeSignature;

  // Live-updating loop range setter that seamlessly fits playback when modified while playing
  const setLoopRangeSafe = useCallback(
    (updater: LoopRange | ((prev: LoopRange) => LoopRange)) => {
      setLoopRange((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        loopRangeRef.current = next;

        // If currently playing, adapt live!
        if (transportStateRef.current === 'playing' && next.enabled) {
          const measureTicks = getTicksPerMeasure(timeSignatureRef.current, ppq);
          const loopStartTick = (next.startMeasure - 1) * measureTicks;
          const loopEndTick = next.endMeasure * measureTicks;
          const cur = currentTickRef.current;

          const startChanged = prev.startMeasure !== next.startMeasure;
          const outsideRange = cur < loopStartTick || cur >= loopEndTick;

          // If L changed, or playhead is outside the new loop range [L, R), fit live immediately
          if (startChanged || outsideRange) {
            audioEngineRef.current.stopAll();
            playedNoteIdsRef.current.clear();
            lastBeatClickedRef.current = -9999;
            clockStartRef.current = {
              perfStart: performance.now(),
              startTick: loopStartTick,
            };
            currentTickRef.current = loopStartTick;
            setCurrentTick(loopStartTick);
          }
        }

        return next;
      });
    },
    [ppq]
  );

  // Quantization state
  const [quantizeOptions, setQuantizeOptions] = useState<QuantizeOptions>({
    grid: '1/16',
    strength: 1.0,
    quantizeStart: true,
    quantizeDuration: false,
  });

  // Notes state & History (for Undo / Redo)
  const [rawNotes, setRawNotes] = useState<MidiNote[]>(() =>
    initialNotes.map((n, i) => ({
      ...n,
      id: n.id || `note-${Date.now()}-${i}`,
      rawStartTick: n.rawStartTick ?? n.startTick,
      rawDurationTicks: n.rawDurationTicks ?? n.durationTicks,
    }))
  );

  const [history, setHistory] = useState<MidiNote[][]>([[]]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);

  // Push new state to history stack
  const commitNotesChange = useCallback((newNotes: MidiNote[]) => {
    setRawNotes(newNotes);
    setHistory((prev) => {
      const sliced = prev.slice(0, historyIndex + 1);
      return [...sliced, newNotes];
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  // Live non-history update for smooth 60fps dragging and left/right resizing preview
  const updateRawNotesLive = useCallback((liveNotes: MidiNote[]) => {
    setRawNotes(liveNotes);
  }, []);

  const undo = useCallback(() => {
    if (historyIndex > 0) {
      const prevIndex = historyIndex - 1;
      setHistoryIndex(prevIndex);
      setRawNotes(history[prevIndex]);
    }
  }, [history, historyIndex]);

  const redo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      const nextIndex = historyIndex + 1;
      setHistoryIndex(nextIndex);
      setRawNotes(history[nextIndex]);
    }
  }, [history, historyIndex]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  // Active notes being held down right now by MIDI input
  const activeHeldNotesRef = useRef<
    Map<
      number,
      {
        tempId: string;
        pitch: number;
        velocity: number;
        startTick: number;
        channel: number;
        audioRelease: (stopTime?: number) => void;
      }
    >
  >(new Map());

  // Refs for requestAnimationFrame clock loop
  const rafIdRef = useRef<number | null>(null);
  const clockStartRef = useRef<{
    perfStart: number;
    startTick: number;
  } | null>(null);

  // Playback note scheduling tracker
  const playedNoteIdsRef = useRef<Set<string>>(new Set());
  const lastBeatClickedRef = useRef<number>(-9999);

  // Computed Quantized Notes (Memoized to prevent new array reference on every render)
  const quantizedNotes = useMemo(() => {
    return quantizeNotes(rawNotes, quantizeOptions, ppq);
  }, [rawNotes, quantizeOptions, ppq]);

  // Ref to track last emitted export signature to prevent infinite update depth loop
  const lastExportSignatureRef = useRef<string>('');

  // Trigger host onChange with exact contract format
  useEffect(() => {
    if (!onChange) return;

    // Compact unique signature of exported state
    const signature = `${ppq}:${bpm}:${timeSignature.beats}/${timeSignature.beatType}:${quantizeOptions.grid}:${
      quantizedNotes.length
    }:${quantizedNotes.map((n) => `${n.pitch},${n.velocity},${n.startTick},${n.durationTicks}`).join(';')}`;

    if (signature === lastExportSignatureRef.current) {
      return;
    }
    lastExportSignatureRef.current = signature;

    const exportData: MidiExportData = {
      ppq,
      bpm,
      timeSignature,
      quantizeGrid: quantizeOptions.grid,
      notes: quantizedNotes.map((n) => ({
        pitch: n.pitch,
        velocity: n.velocity,
        startTick: n.startTick,
        durationTicks: n.durationTicks,
        channel: n.channel,
      })),
    };
    onChange(exportData);
  }, [quantizedNotes, bpm, timeSignature.beats, timeSignature.beatType, quantizeOptions.grid, ppq, onChange]);

  // Derived current position object
  const currentPosition: TransportPosition = ticksToPosition(
    Math.max(0, currentTick),
    timeSignature,
    bpm,
    ppq
  );

  // Keep refs for live values to prevent race conditions or missed audio frames
  const metronomeEnabledRef = useRef<boolean>(metronomeEnabled);
  metronomeEnabledRef.current = metronomeEnabled;
  const quantizedNotesRef = useRef<MidiNote[]>(quantizedNotes);
  quantizedNotesRef.current = quantizedNotes;

  // Metronome click helper
  const checkAndPlayMetronome = useCallback(
    (tick: number) => {
      if (!metronomeEnabledRef.current) return;
      const beatTicks = getTicksPerBeat(ppq, timeSignature.beatType);
      const beatIndex = Math.floor(tick / beatTicks);

      if (beatIndex !== lastBeatClickedRef.current) {
        lastBeatClickedRef.current = beatIndex;
        const isFirstBeat = (beatIndex % timeSignature.beats) === 0;
        audioEngineRef.current.playClick(isFirstBeat);
      }
    },
    [ppq, timeSignature]
  );

  // Stop transport helper
  const stop = useCallback(() => {
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    clockStartRef.current = null;
    setTransportState('stopped');
    setCountInBeat(null);
    audioEngineRef.current.stopAll();
    playedNoteIdsRef.current.clear();
    lastBeatClickedRef.current = -9999;

    // Finalize any currently held notes
    if (activeHeldNotesRef.current.size > 0) {
      const nowTick = currentTick;
      const finished: MidiNote[] = [];
      for (const held of activeHeldNotesRef.current.values()) {
        held.audioRelease();
        finished.push({
          id: held.tempId,
          pitch: held.pitch,
          velocity: held.velocity,
          startTick: held.startTick,
          durationTicks: Math.max(20, nowTick - held.startTick),
          channel: held.channel,
          rawStartTick: held.startTick,
          rawDurationTicks: Math.max(20, nowTick - held.startTick),
        });
      }
      activeHeldNotesRef.current.clear();
      if (finished.length > 0) {
        setRawNotes((prev) => [...prev, ...finished]);
      }
    }
  }, [currentTick]);

  // Play transport helper (Sample-accurate Web Audio lookahead scheduling)
  const play = useCallback(() => {
    stop();
    const ctx = audioEngineRef.current.getContext();
    if (ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
    playedNoteIdsRef.current.clear();
    lastBeatClickedRef.current = -9999;
    setTransportState('playing');

    const currentMeasureTicks = getTicksPerMeasure(timeSignatureRef.current, ppq);
    const currentLoop = loopRangeRef.current;
    const loopStartTick = currentLoop.enabled
      ? (currentLoop.startMeasure - 1) * currentMeasureTicks
      : 0;

    // When loop is active, start playback on Left handle
    const startTick = currentLoop.enabled ? loopStartTick : currentTickRef.current;
    setCurrentTick(startTick);
    currentTickRef.current = startTick;

    const perfStart = performance.now();
    clockStartRef.current = { perfStart, startTick };

    // Lookahead window of 100ms
    const lookaheadMs = 100;

    const scheduleLookahead = (fromTick: number, currentPlayheadTick: number) => {
      const currentBpm = bpmRef.current;
      const currentTimeSig = timeSignatureRef.current;
      const measureTicks = getTicksPerMeasure(currentTimeSig, ppq);
      const lookaheadTicks = msToTicks(lookaheadMs, currentBpm, ppq);
      const scheduleUntilTick = currentPlayheadTick + lookaheadTicks;
      const nowCtx = ctx.currentTime;

      const liveLoop = loopRangeRef.current;
      const liveLoopStartTick = liveLoop.enabled
        ? (liveLoop.startMeasure - 1) * measureTicks
        : 0;
      const liveLoopEndTick = liveLoop.enabled
        ? liveLoop.endMeasure * measureTicks
        : Infinity;
      const effectiveScheduleUntil = liveLoop.enabled
        ? Math.min(scheduleUntilTick, liveLoopEndTick)
        : scheduleUntilTick;

      for (const note of quantizedNotesRef.current) {
        if (playedNoteIdsRef.current.has(note.id)) continue;

        // If loop is active, only schedule notes within the active loop boundary
        if (liveLoop.enabled && (note.startTick < liveLoopStartTick || note.startTick >= liveLoopEndTick)) {
          continue;
        }

        // Must be at or after playback start / segment start (never skip notes at beginning of measure)
        if (note.startTick < fromTick - 0.5) {
          continue;
        }

        if (note.startTick <= effectiveScheduleUntil) {
          playedNoteIdsRef.current.add(note.id);
          const deltaTicks = Math.max(0, note.startTick - currentPlayheadTick);
          const deltaSec = ticksToMs(deltaTicks, currentBpm, ppq) / 1000;
          const scheduledStartTime = nowCtx + deltaSec;
          const durSec = ticksToMs(note.durationTicks, currentBpm, ppq) / 1000;
          audioEngineRef.current.scheduleNote(note.pitch, note.velocity, scheduledStartTime, durSec);
        }
      }
    };

    // IMMEDIATELY schedule the first lookahead batch so notes right at startTick are never skipped
    scheduleLookahead(startTick, startTick);

    // Initial metronome click on start if enabled and on beat
    if (metronomeEnabledRef.current) {
      const beatTicks = getTicksPerBeat(ppq, timeSignatureRef.current.beatType);
      const beatIndex = Math.floor(startTick / beatTicks);
      lastBeatClickedRef.current = beatIndex;
      const isFirstBeat = (beatIndex % timeSignatureRef.current.beats) === 0;
      audioEngineRef.current.playClick(isFirstBeat);
    }

    const tickFrame = () => {
      if (!clockStartRef.current) return;
      const currentBpm = bpmRef.current;
      const currentTimeSig = timeSignatureRef.current;
      const measureTicks = getTicksPerMeasure(currentTimeSig, ppq);
      const beatTicks = getTicksPerBeat(ppq, currentTimeSig.beatType);

      const elapsedMs = performance.now() - clockStartRef.current.perfStart;
      const elapsedTicks = msToTicks(elapsedMs, currentBpm, ppq);
      let newTick = clockStartRef.current.startTick + elapsedTicks;

      // Dynamic Loop Handling: always evaluate live loop range from ref
      const liveLoop = loopRangeRef.current;
      if (liveLoop.enabled) {
        const liveLoopStartTick = (liveLoop.startMeasure - 1) * measureTicks;
        const liveLoopEndTick = liveLoop.endMeasure * measureTicks;

        // If playhead reached or passed R, or fell before L due to live adjustments
        if (newTick >= liveLoopEndTick || newTick < liveLoopStartTick) {
          newTick = liveLoopStartTick;
          clockStartRef.current = {
            perfStart: performance.now(),
            startTick: liveLoopStartTick,
          };
          audioEngineRef.current.stopAll();
          playedNoteIdsRef.current.clear();
          lastBeatClickedRef.current = -9999;
          // Immediately schedule notes at the beginning of the new loop iteration
          scheduleLookahead(liveLoopStartTick, liveLoopStartTick);
        }
      }

      currentTickRef.current = newTick;
      setCurrentTick(newTick);

      // Metronome Click during playback (only if metronome is enabled)
      if (metronomeEnabledRef.current) {
        const beatIndex = Math.floor(newTick / beatTicks);
        if (beatIndex !== lastBeatClickedRef.current) {
          lastBeatClickedRef.current = beatIndex;
          const isFirstBeat = (beatIndex % currentTimeSig.beats) === 0;
          audioEngineRef.current.playClick(isFirstBeat);
        }
      }

      // Schedule notes in lookahead window from the current segment start
      const segmentStart = clockStartRef.current.startTick;
      scheduleLookahead(segmentStart, newTick);

      rafIdRef.current = requestAnimationFrame(tickFrame);
    };

    rafIdRef.current = requestAnimationFrame(tickFrame);
  }, [stop, ppq]);

  // Record transport helper (with Count-in support)
  const record = useCallback(() => {
    stop();
    audioEngineRef.current.getContext();
    playedNoteIdsRef.current.clear();
    lastBeatClickedRef.current = -9999;

    const measureTicks = getTicksPerMeasure(timeSignature, ppq);
    const beatTicks = getTicksPerBeat(ppq, timeSignature.beatType);
    const totalCountInTicks = countInMeasures * measureTicks;

    if (totalCountInTicks > 0) {
      setTransportState('count-in');
      const countInStartTick = -totalCountInTicks;
      setCurrentTick(countInStartTick);

      const countInPerfStart = performance.now();
      clockStartRef.current = {
        perfStart: countInPerfStart,
        startTick: countInStartTick,
      };

      const countInFrame = () => {
        if (!clockStartRef.current) return;
        const elapsedMs = performance.now() - clockStartRef.current.perfStart;
        const elapsedTicks = msToTicks(elapsedMs, bpm, ppq);
        const tick = clockStartRef.current.startTick + elapsedTicks;

        setCurrentTick(tick);

        // Calculate count-in beat indicator (e.g. 1, 2, 3, 4)
        if (tick < 0) {
          const ticksUntilZero = -tick;
          const beatsUntilZero = Math.ceil(ticksUntilZero / beatTicks);
          const beatInBar = timeSignature.beats - ((beatsUntilZero - 1) % timeSignature.beats);
          setCountInBeat(beatInBar);
          checkAndPlayMetronome(tick);
          rafIdRef.current = requestAnimationFrame(countInFrame);
        } else {
          // Count-in finished! Seamlessly switch to recording at tick 0
          setCountInBeat(null);
          setTransportState('recording');
          clockStartRef.current = {
            perfStart: performance.now(),
            startTick: 0,
          };
          setCurrentTick(0);
          lastBeatClickedRef.current = -9999;

          const recordFrame = () => {
            if (!clockStartRef.current) return;
            const recElapsed = performance.now() - clockStartRef.current.perfStart;
            const recTicks = msToTicks(recElapsed, bpm, ppq);
            const curTick = clockStartRef.current.startTick + recTicks;
            setCurrentTick(curTick);
            checkAndPlayMetronome(curTick);
            rafIdRef.current = requestAnimationFrame(recordFrame);
          };

          rafIdRef.current = requestAnimationFrame(recordFrame);
        }
      };

      rafIdRef.current = requestAnimationFrame(countInFrame);
    } else {
      // No count-in: start recording immediately at tick 0 or current position
      setTransportState('recording');
      clockStartRef.current = {
        perfStart: performance.now(),
        startTick: Math.max(0, currentTick),
      };

      const recordFrame = () => {
        if (!clockStartRef.current) return;
        const elapsedMs = performance.now() - clockStartRef.current.perfStart;
        const elapsedTicks = msToTicks(elapsedMs, bpm, ppq);
        const tick = clockStartRef.current.startTick + elapsedTicks;
        setCurrentTick(tick);
        checkAndPlayMetronome(tick);
        rafIdRef.current = requestAnimationFrame(recordFrame);
      };

      rafIdRef.current = requestAnimationFrame(recordFrame);
    }
  }, [
    stop,
    timeSignature,
    ppq,
    countInMeasures,
    bpm,
    currentTick,
    checkAndPlayMetronome,
  ]);

  // Seek position
  const seekToTick = useCallback(
    (targetTick: number) => {
      const cleanTick = Math.max(0, targetTick);
      currentTickRef.current = cleanTick;
      setCurrentTick(cleanTick);
      if (transportStateRef.current === 'playing' || transportStateRef.current === 'recording') {
        audioEngineRef.current.stopAll();
        clockStartRef.current = {
          perfStart: performance.now(),
          startTick: cleanTick,
        };
        playedNoteIdsRef.current.clear();
        lastBeatClickedRef.current = -9999;
      }
    },
    []
  );

  const seekToMeasure = useCallback(
    (measureIndex1Based: number) => {
      const measureTicks = getTicksPerMeasure(timeSignatureRef.current, ppq);
      const targetTick = (Math.max(1, measureIndex1Based) - 1) * measureTicks;
      seekToTick(targetTick);
    },
    [ppq, seekToTick]
  );

  // Transport navigation: << (Rewind), >> (Fast Forward), |< (Return to Start)
  const rewind = useCallback(() => {
    const measureTicks = getTicksPerMeasure(timeSignatureRef.current, ppq);
    const curTick = currentTickRef.current;
    const currentM = Math.floor(curTick / measureTicks);
    const offsetInMeasure = curTick % measureTicks;

    let targetTick: number;
    if (offsetInMeasure > measureTicks * 0.25) {
      // Rewind to beginning of current measure
      targetTick = currentM * measureTicks;
    } else {
      // Rewind to beginning of previous measure
      targetTick = Math.max(0, (currentM - 1) * measureTicks);
    }
    seekToTick(targetTick);
  }, [ppq, seekToTick]);

  const fastForward = useCallback(() => {
    const measureTicks = getTicksPerMeasure(timeSignatureRef.current, ppq);
    const curTick = currentTickRef.current;
    const currentM = Math.floor(curTick / measureTicks);
    const targetTick = (currentM + 1) * measureTicks;
    seekToTick(targetTick);
  }, [ppq, seekToTick]);

  const returnToStart = useCallback(() => {
    if (loopRangeRef.current.enabled) {
      const measureTicks = getTicksPerMeasure(timeSignatureRef.current, ppq);
      const loopStartTick = (loopRangeRef.current.startMeasure - 1) * measureTicks;
      if (currentTickRef.current === loopStartTick) {
        seekToTick(0);
      } else {
        seekToTick(loopStartTick);
      }
    } else {
      seekToTick(0);
    }
  }, [ppq, seekToTick]);

  // Handle live MIDI Note On / Note Off
  useEffect(() => {
    const unsubscribeOn = midiAccessRef.current.onNoteOn((pitch, velocity, channel) => {
      // Always play audio monitor so user hears what they play
      const audioRelease = audioEngineRef.current.playNoteOn(pitch, velocity);
      const state = transportStateRef.current;
      const tick = currentTickRef.current;

      // Track active sounding pitch for UI keyboard reaction
      setLivePitches((prev) => {
        const next = new Set(prev);
        next.add(pitch);
        return next;
      });

      setLastMidiActivity({
        pitch,
        velocity,
        channel,
        type: 'on',
        timestamp: Date.now(),
      });

      if (state === 'recording') {
        const startTick = Math.max(0, tick);
        const tempId = `note-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`;

        activeHeldNotesRef.current.set(pitch, {
          tempId,
          pitch,
          velocity,
          startTick,
          channel,
          audioRelease,
        });

        // Add note placeholder immediately so it renders in real-time
        setRawNotes((prev) => [
          ...prev,
          {
            id: tempId,
            pitch,
            velocity,
            startTick,
            durationTicks: 80, // initial visible stub while holding
            channel,
            rawStartTick: startTick,
            rawDurationTicks: 80,
          },
        ]);
      } else {
        activeHeldNotesRef.current.set(pitch, {
          tempId: `audition-${pitch}`,
          pitch,
          velocity,
          startTick: 0,
          channel,
          audioRelease,
        });
      }
    });

    const unsubscribeOff = midiAccessRef.current.onNoteOff((pitch, channel) => {
      setLivePitches((prev) => {
        if (!prev.has(pitch)) return prev;
        const next = new Set(prev);
        next.delete(pitch);
        return next;
      });

      setLastMidiActivity({
        pitch,
        velocity: 0,
        channel,
        type: 'off',
        timestamp: Date.now(),
      });

      const held = activeHeldNotesRef.current.get(pitch);
      if (held) {
        held.audioRelease();
        activeHeldNotesRef.current.delete(pitch);
        const state = transportStateRef.current;
        const tick = currentTickRef.current;

        if (state === 'recording') {
          const finishedDur = Math.max(20, tick - held.startTick);
          setRawNotes((prev) =>
            prev.map((n) =>
              n.id === held.tempId
                ? {
                    ...n,
                    durationTicks: finishedDur,
                    rawDurationTicks: finishedDur,
                  }
                : n
            )
          );
        }
      }
    });

    return () => {
      unsubscribeOn();
      unsubscribeOff();
    };
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      audioEngineRef.current.stopAll();
    };
  }, []);

  return {
    // Transport & Clock
    bpm,
    setBpm,
    timeSignature,
    setTimeSignature,
    ppq,
    transportState,
    currentTick,
    currentPosition,
    countInMeasures,
    setCountInMeasures,
    countInBeat,
    metronomeEnabled,
    setMetronomeEnabled,
    loopRange,
    setLoopRange: setLoopRangeSafe,
    play,
    stop,
    record,
    seekToTick,
    seekToMeasure,
    rewind,
    fastForward,
    returnToStart,

    // Notes & Quantization
    rawNotes,
    quantizedNotes,
    commitNotesChange,
    updateRawNotesLive,
    quantizeOptions,
    setQuantizeOptions,

    // History
    undo,
    redo,
    canUndo,
    canRedo,

    // Audio & Midi instances
    audioEngine: audioEngineRef.current,
    midiAccess: midiAccessRef.current,
    livePitches,
    lastMidiActivity,
  };
}
